// State of one machine in the app (AD-5, AD-34): snapshot first, then only newer (boot, seq) per group; provisional
// values from commands until a state message at or after the ack's exec arrives (at most 10 s); cloud-owned flags only
// with a higher cloud_rev (AD-44). No React in here.
import { compareBootSeq, findCommand, orioneCommandCatalog, type CommandCatalog } from '@brewcore/protocol';
import type {
  ConnInfo,
  ConnMessage,
  FlagsMessage,
  GroupEntry,
  LiveDeltaMessage,
  MachineSummary,
  PendingCommand,
  Position,
  ProfileDeltaMessage,
  SnapshotMessage,
  StateDeltaMessage,
} from '@brewcore/app-contract';
import { interruptedBy, type Interrupted } from './interrupted.js';
import { liveOf, nextReadyHeld, READY_BAND_K } from './status.js';

export type ParamValue = boolean | number | string;

export interface Provisional {
  value: ParamValue;
  cmdId: string;
  /** set by the ack: the machine's position at execution; a group state >= exec contains the effect */
  exec: Position | null;
  expiresAt: number;
}

export interface MachineView {
  ready: boolean;
  info: MachineSummary | null;
  conn: ConnInfo;
  profile: GroupEntry | null;
  groups: Readonly<Record<string, GroupEntry>>;
  live: GroupEntry | null;
  dataLost: boolean;
  remoteLocked: boolean;
  cloudRev: number;
  pendingOnServer: PendingCommand[];
  provisional: ReadonlyMap<string, Provisional>;
  /** Unix ms of the last change (for "Stand von ...") */
  updatedAt: number;
  /**
   * Cloud clock minus this device's clock (ms), from the last snapshot. Times the API stamps (live receivedAt,
   * conn.since) minus this offset are times of this device's clock: ages stay right on a phone whose clock is off.
   */
  clockOffset: number;
  /** This device's clock when its connection to the cloud dropped; null while connected (calm display, core/calm.ts). */
  linkLostAt: number | null;
  /** the API ended this user's access to the machine (revoked): the values stay, control is gone for good */
  accessEnded: boolean;
  /** a shot or the descaling programme this device saw running, cut short by a restart (D-G02, core/interrupted.ts) */
  interrupted?: Interrupted | null;
  /**
   * whether the temperature counted as ready at the last values (core/status.ts nextReadyHeld): the round display keeps
   * saying ready up to 0.3 K further once it was, and the app says the same on every tab
   */
  readyHeld?: boolean;
}

export const PROVISIONAL_MAX_MS = 10_000;

const initialConn: ConnInfo = { state: 'unknown', since: 0, lastSeenAt: null, boot: null };

export class MachineStore {
  private view: MachineView;
  private readonly listeners = new Set<() => void>();
  /** every accepted (group, boot, seq), for tests of the "never older" rule */
  readonly accepted: { group: string; boot: number; seq: number }[] = [];
  recordAccepted = false;

  constructor(
    readonly machineId: string,
    private readonly catalog: CommandCatalog = orioneCommandCatalog,
    private readonly now: () => number = Date.now,
  ) {
    this.view = {
      ready: false,
      info: null,
      conn: initialConn,
      profile: null,
      groups: {},
      live: null,
      dataLost: false,
      remoteLocked: false,
      cloudRev: -1,
      pendingOnServer: [],
      provisional: new Map(),
      updatedAt: 0,
      clockOffset: 0,
      linkLostAt: null,
      accessEnded: false,
    };
  }

  // ---- subscription (useSyncExternalStore compatible) ----

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): MachineView => this.view;

  /** `touch` false: no new data (a lost connection), "Stand von …" keeps the time of the last data. */
  private commit(patch: Partial<MachineView>, touch = true): void {
    const next: MachineView = { ...this.view, ...patch, ...(touch ? { updatedAt: this.now() } : {}) };
    const num = (id: string) => {
      const v = this.valueIn(next, id);
      return typeof v === 'number' ? v : undefined;
    };
    const band = num('display.blinking.delta');
    next.readyHeld = nextReadyHeld(this.view.readyHeld ?? false, liveOf(next, this.now()), {
      setpoint: num('brew.setpoint'),
      warm: num('standby.warm_temp') ?? 0,
      band: band !== undefined && band > 0 ? band : READY_BAND_K,
    });
    this.view = next;
    for (const fn of this.listeners) fn();
  }

  // ---- incoming ----

  applySnapshot(s: SnapshotMessage): void {
    const groups: Record<string, GroupEntry> = { ...this.view.groups };
    for (const [g, entry] of Object.entries(s.groups)) {
      if (this.newer(g, entry, groups[g])) groups[g] = entry;
    }
    const cut = s.groups.op ? this.interruptedOf(s.groups.op) : null;
    const profile = this.newer('_profile', s.profile, this.view.profile) ? s.profile : this.view.profile;
    const live = this.newer('_live', s.live, this.view.live) ? s.live : this.view.live;
    const flags =
      s.cloudRev >= this.view.cloudRev
        ? { dataLost: s.dataLost, remoteLocked: s.remoteLocked, cloudRev: s.cloudRev }
        : {};
    this.commit({
      ready: true,
      info: s.info,
      conn: s.conn,
      profile,
      groups,
      live,
      pendingOnServer: s.pending,
      clockOffset: s.serverTime - this.now(),
      linkLostAt: null,
      accessEnded: false,
      ...(cut ? { interrupted: cut } : {}),
      ...flags,
    });
    this.resolveProvisional();
  }

  /** A newer boot after a running shot or programme (only against the op group this store had). */
  private interruptedOf(op: GroupEntry): Interrupted | null {
    const prev = this.view.groups.op;
    return prev && compareBootSeq(op, prev) > 0 ? interruptedBy(prev, op, this.now()) : null;
  }

  applyState(d: StateDeltaMessage): void {
    const entry: GroupEntry = { boot: d.boot, seq: d.seq, data: d.data, receivedAt: d.receivedAt };
    const cut = d.group === 'op' ? this.interruptedOf(entry) : null;
    if (!this.newer(d.group, entry, this.view.groups[d.group])) return;
    this.commit({ groups: { ...this.view.groups, [d.group]: entry }, ...(cut ? { interrupted: cut } : {}) });
    this.resolveProvisional();
  }

  applyProfile(d: ProfileDeltaMessage): void {
    const entry: GroupEntry = { boot: d.boot, seq: d.seq, data: d.data, receivedAt: d.receivedAt };
    if (!this.newer('_profile', entry, this.view.profile)) return;
    this.commit({ profile: entry });
  }

  applyLive(d: LiveDeltaMessage): void {
    const entry: GroupEntry = { boot: d.boot, seq: d.seq, data: d.data, receivedAt: d.receivedAt };
    if (!this.newer('_live', entry, this.view.live)) return;
    this.commit({ live: entry });
  }

  applyConn(d: ConnMessage): void {
    this.commit({ conn: d.conn });
  }

  applyFlags(d: FlagsMessage): void {
    if (d.cloudRev < this.view.cloudRev) return;
    this.commit({ dataLost: d.dataLost, remoteLocked: d.remoteLocked, cloudRev: d.cloudRev });
  }

  /**
   * Connection to the cloud lost: values stay visible, control waits for the next snapshot (FR-21). `at` is when it
   * dropped (default now); a gap shows on screen only once it lasts (core/calm.ts).
   */
  markDisconnected(at: number = this.now()): void {
    if (this.view.ready || this.view.linkLostAt === null)
      this.commit({ ready: false, linkLostAt: this.view.linkLostAt ?? at }, false);
  }

  /** Access to the machine ended (revoked): no control, but no lost connection either (the API answers commands). */
  markRevoked(): void {
    this.commit({ ready: false, accessEnded: true }, false);
  }

  private newer(
    group: string,
    candidate: GroupEntry | null | undefined,
    current: GroupEntry | null | undefined,
  ): candidate is GroupEntry {
    if (!candidate) return false;
    if (current && compareBootSeq(candidate, current) <= 0) return false;
    if (this.recordAccepted) this.accepted.push({ group, boot: candidate.boot, seq: candidate.seq });
    return true;
  }

  // ---- provisional values ----

  setProvisional(params: Record<string, ParamValue>, cmdId: string): void {
    const next = new Map(this.view.provisional);
    for (const [k, v] of Object.entries(params))
      next.set(k, { value: v, cmdId, exec: null, expiresAt: this.now() + PROVISIONAL_MAX_MS });
    this.commit({ provisional: next });
  }

  /** ack done: the effective values stay provisional until a state at or after exec arrives. */
  confirmProvisional(
    cmdId: string,
    effective: Record<string, ParamValue> | undefined,
    exec: Position | undefined,
  ): void {
    const next = new Map(this.view.provisional);
    for (const [k, p] of next) {
      if (p.cmdId !== cmdId) continue;
      const value = effective && k in effective ? (effective[k] as ParamValue) : p.value;
      // AD-5: values from an ack never win over a group the app already has newer
      const group = this.groupOf(k);
      const current = group ? this.view.groups[group] : undefined;
      if (exec && current && compareBootSeq(current, exec) >= 0) next.delete(k);
      else next.set(k, { ...p, value, exec: exec ?? null });
    }
    this.commit({ provisional: next });
  }

  /** rejected, expired, stale, no response, unconfirmed: back to the machine's value. */
  rollback(cmdId: string): void {
    const next = new Map([...this.view.provisional].filter(([, p]) => p.cmdId !== cmdId));
    if (next.size !== this.view.provisional.size) this.commit({ provisional: next });
  }

  /** Drops provisional values whose effect is in the mirror or that are older than 10 s. */
  resolveProvisional(): void {
    const now = this.now();
    let changed = false;
    const next = new Map(this.view.provisional);
    for (const [k, p] of next) {
      const group = this.groupOf(k);
      const current = group ? this.view.groups[group] : undefined;
      if ((p.exec && current && compareBootSeq(current, p.exec) >= 0) || p.expiresAt <= now) {
        next.delete(k);
        changed = true;
      }
    }
    if (changed) this.commit({ provisional: next });
  }

  // ---- reading ----

  groupOf(param: string): string | undefined {
    return findCommand(this.catalog, param)?.group;
  }

  /** The machine's value (mirror) without provisional overlay. */
  machineValue(param: string): ParamValue | undefined {
    return this.valueIn(this.view, param);
  }

  private valueIn(view: MachineView, param: string): ParamValue | undefined {
    const group = this.groupOf(param);
    const values = group
      ? (view.groups[group]?.data.values as Record<string, ParamValue> | undefined)
      : undefined;
    return values?.[param];
  }

  /** What the app shows: a provisional value while a command is in flight, else the machine's value. */
  value(param: string): ParamValue | undefined {
    const p = this.view.provisional.get(param);
    if (p && p.expiresAt > this.now()) return p.value;
    return this.machineValue(param);
  }

  op(): Record<string, unknown> | undefined {
    return this.view.groups.op?.data;
  }
}
